import React from 'react';
import { Range, Root, Thumb, Track } from '@radix-ui/react-slider';
import { throttle } from 'lodash-es';
import { styled } from '@components-shared/src/stitches';
import { formatTime } from '@components/AudioPlayer/utils';
import { seek, useAudioController } from '@store/audio/AudioController';
import { useAudioPosition } from '@store/audio/AudioPosition';

const THROTTLE_INTERVAL = 100;

export const AudioProgress = () => {
  const { duration } = useAudioController();
  const { position } = useAudioPosition();

  const [scrubPosition, setScrubPosition] = React.useState<number>(position);
  const [isScrubbing, setIsScrubbing] = React.useState(false);
  const timeoutId = React.useRef<ReturnType<typeof setTimeout>>();

  const throttledScrubPos = React.useMemo(() => {
    return throttle(
      (position: number) => {
        setScrubPosition(position);
      },
      THROTTLE_INTERVAL,
      {
        leading: true,
        trailing: true,
      },
    );
  }, [setScrubPosition]);

  React.useEffect(() => {
    if (isScrubbing) return;

    throttledScrubPos(position);
  }, [position, throttledScrubPos, isScrubbing]);

  const sliderTrack = React.useMemo(() => {
    return (
      <>
        <SliderTrack>
          <SliderRange />
        </SliderTrack>
        <StyledThumb />
      </>
    );
  }, []);

  return (
    <Container>
      <AudioSlider
        aria-label="Audio Slider"
        value={[scrubPosition]}
        min={0}
        max={duration}
        onValueChange={value => {
          const pos = typeof value === 'number' ? value : value[0];
          if (pos) {
            setIsScrubbing(true);
            setScrubPosition(pos);
          }
        }}
        onValueCommit={() => {
          seek(scrubPosition);
          if (timeoutId.current) clearTimeout(timeoutId.current);
          timeoutId.current = setTimeout(() => setIsScrubbing(false), THROTTLE_INTERVAL);
        }}
      >
        {sliderTrack}
      </AudioSlider>

      <DurationContainer>
        <span>{formatTime(scrubPosition)}</span>
        <span>{formatTime(duration)}</span>
      </DurationContainer>
    </Container>
  );
};

const Container = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  gap: 12,
  zIndex: '$above2',
  overflow: 'hidden',
  paddingTop: 8,
});

const DurationContainer = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  fontFamily: '$base',
  fontSize: 10,
  fontWeight: 400,
  lineHeight: '14px',
  color: '$white',
});

const AudioSlider = styled(Root, {
  display: ' flex',
  alignItems: ' center',
  width: '100%',
  position: 'relative',
});

const SliderTrack = styled(Track, {
  backgroundColor: '$base600',
  position: 'relative',
  flexGrow: 1,
  height: '4px',
  borderRadius: 10,
});

const SliderRange = styled(Range, {
  backgroundColor: '$white',
  position: 'absolute',
  borderRadius: '10px 0 0 10px',
  height: '100%',
});

const StyledThumb = styled(Thumb, {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '50%',
  size: 16,
  outline: 'none',
  boxSizing: 'content-box',
  backgroundColor: '$white',
  cursor: 'pointer',
  '&:active': {
    transform: 'scale(1.2)',
  },

  '&:before': {
    content: ' ',
    background: 'transparent',
    borderRadius: '50%',
    size: 56,
    flexShrink: 0,
    ouline: '2px solid $white',
  },
});
